import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Tooltip,
  Dialog,
  IconButton,
} from '@mui/material';
import {
  FolderOutlined,
  ShareOutlined,
  Add as AddIcon,
  AutoFixHigh as AutoFixHighIcon,
  SentimentSatisfiedAlt as SentimentIcon,
  TrendingUp as TrendingUpIcon,
  GroupOutlined as CollaborateIcon,
  ArrowForward as ArrowForwardIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Close as CloseIcon,
  ListAltOutlined,
  AssignmentOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useTenant } from '../../contexts/TenantContext';
import useApi from '../../hooks/useApi';
import { LAYOUT } from '../../utils/layoutConstants';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { canAccessDashboardRoutes } from '../../constants/dashboardAccessPermissions';
import {
  canAccessSurveyModule,
  canAccessSurveyManage,
  canAccessSurveyResponses,
  SDA_CREATE_SURVEY,
} from '../../constants/surveyAccessPermissions';
import './HomePage.css';


const capabilities = [
  {
    icon: <AutoFixHighIcon sx={{ fontSize: 18 }} />,
    title: 'AI-Powered Analysis',
    
  },
  {
    icon: <SentimentIcon sx={{ fontSize: 18 }} />,
    title: 'Sentiment Analysis',
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
    title: 'Smart Visualizations',
  },
  {
    icon: <CollaborateIcon sx={{ fontSize: 18 }} />,
    title: 'Collaboration',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { currentTenant, hasPermission } = useTenant();
  const firstName = authState?.idToken?.claims?.given_name ||
                    authState?.idToken?.claims?.name?.split(' ')[0] ||
                    'there';
  const lastName = authState?.idToken?.claims?.family_name ||
                    authState?.idToken?.claims?.name?.split(' ')[1] ||
                    '';
  const [myDashboardsCount, setMyDashboardsCount] = useState(0);
  const [sharedDashboardsCount, setSharedDashboardsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDemo, setShowDemo] = useState(false);
  const videoRef = useRef(null);

  const canSeeDashboardList = canAccessDashboardRoutes(hasPermission);
  const canCreateDashboard = hasPermission('create_dashboard');
  const canSeeSharedDashboards = hasPermission('view_shared_dashboards');
  const showDashboardsSection = canSeeDashboardList || canCreateDashboard || canSeeSharedDashboards;

  const canSeeSurveyModule = canAccessSurveyModule(hasPermission);
  const canCreateSurvey = hasPermission(SDA_CREATE_SURVEY);
  const canManageSurveys = canAccessSurveyManage(hasPermission);
  const canViewSurveyResponses = canAccessSurveyResponses(hasPermission);
  const showSurveysSection = canSeeSurveyModule || canCreateSurvey || canManageSurveys || canViewSurveyResponses;

  useEffect(() => {
    const tenantId = currentTenant?.tenantId;
    if (!tenantId || !canSeeDashboardList) {
      setLoading(false);
      setMyDashboardsCount(0);
      setSharedDashboardsCount(0);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);

        const myDashboardsResponse = await apiRef.current.get('/dashboards', {
          headers: { 'X-Tenant-ID': tenantId },
        });
        if (cancelled) return;
        if (myDashboardsResponse.data?.success) {
          const dashboards = myDashboardsResponse.data.data?.dashboards || [];
          setMyDashboardsCount(dashboards.length);
        }

        try {
          const sharedResponse = await apiRef.current.get('/sharing', {
            params: { withMe: '1' },
            headers: { 'X-Tenant-ID': tenantId },
          });
          if (cancelled) return;
          if (sharedResponse.data?.success) {
            const n = sharedResponse.data.data?.totalCount;
            if (typeof n === 'number') {
              setSharedDashboardsCount(n);
            } else {
              const items = sharedResponse.data.data?.items || [];
              setSharedDashboardsCount(items.length);
            }
          }
        } catch (err) {
          if (!cancelled) setSharedDashboardsCount(0);
        }
      } catch (error) {
        console.error('Error loading dashboard counts:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentTenant?.tenantId, hasPermission]);

  return (
    <Box className="home-page">
      {/* ---- Blue Hero Banner with Illustration ---- */}
      <div className="home-hero-banner">
        <img
          className="home-hero-banner-illustration"
          src={`${process.env.PUBLIC_URL}/images/hero-illustration.svg`}
          alt=""
          aria-hidden="true"
        />
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
          <div className="home-hero home-animate-in home-animate-delay-1">
            <h1 className="home-hero-title">
              Transform Survey Data
              <br />
              into <span className="home-hero-accent">Actionable Insights</span> with AI
            </h1>
            <p className="home-hero-subtitle">
            Summarize, analyze, and visualize survey data with AI-powered insights.
            </p>
            <div className="home-hero-cta">
              <button className="home-cta-demo" onClick={() => setShowDemo(true)}>
                <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
                Watch Demo
              </button>
              <a
                className="home-cta-learn"
                href="https://jll2.sharepoint.com/:u:/r/sites/AIInnovation-DataandInsightsCoE/SitePages/Survey-Data-Analytics.aspx?csf=1&web=1&e=PmXG3q"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn More
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </a>
            </div>
          </div>
          <div className="home-capabilities-wrap home-animate-in home-animate-delay-2">
            <hr className="home-capabilities-divider" />
            <div className="home-capabilities-strip">
              {capabilities.map((cap) => (
                <Tooltip key={cap.title} title={cap.desc} arrow placement="bottom">
                  <div className="home-cap-item">
                    <div className="home-cap-item-icon">{cap.icon}</div>
                    <span className="home-cap-item-text">{cap.title}</span>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        </Container>
      </div>

      <div className="home-banner-accent" />

      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* ---- Greeting ---- */}
        <p className="home-hero-greeting home-animate-in home-animate-delay-3">
          Hello, {firstName} {lastName}
        </p>

        {/* ---- Analytics & Dashboards ---- */}
        {showDashboardsSection && (
        <div className="home-cards-row home-animate-in home-animate-delay-3">
          <div className="home-cards-col home-cards-col--start">
            <div className="home-section-header">
              <div className="home-section-accent" />
              <h2 className="home-section-title">Dashboards</h2>
            </div>
            <Tooltip
              title={
                canCreateDashboard
                  ? 'Upload survey data and let AI generate insights'
                  : 'You do not have permission to create dashboards'
              }
              arrow
              placement="bottom"
            >
              <div
                className={`home-action-card${!canCreateDashboard ? ' home-action-card--disabled' : ''}`}
                onClick={canCreateDashboard ? () => navigate('/landing') : undefined}
                role="button"
                tabIndex={canCreateDashboard ? 0 : -1}
                onKeyDown={(e) => {
                  if (canCreateDashboard && (e.key === 'Enter' || e.key === ' ')) navigate('/landing');
                }}
              >
                <div className="home-action-card-icon">
                  <AddIcon sx={{ fontSize: 24, color: '#333' }} />
                </div>
                <div className="home-action-card-title">Create Dashboard</div>
                <div className="home-action-card-desc">
                  Upload survey data and let AI generate insights.
                </div>
              </div>
            </Tooltip>
          </div>

          <div className="home-cards-col home-cards-col--dashboards">
            <div className="home-section-header" style={{ visibility: 'hidden' }} aria-hidden="true">
              <div className="home-section-accent" />
              <h2 className="home-section-title">Dashboards</h2>
            </div>
            <div className="home-cards-pair">
              {canSeeDashboardList && (
              <Tooltip title="View and manage your survey analysis dashboards" arrow placement="bottom">
                <div
                  className="home-action-card"
                  onClick={() => navigate('/dashboards')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigate('/dashboards');
                  }}
                >
                  <div className="home-action-card-icon">
                    <FolderOutlined sx={{ fontSize: 24, color: '#333' }} />
                  </div>
                  <div className="home-action-card-title">Dashboard</div>
                  <div className="home-action-card-desc">
                    {loading
                      ? 'Loading...'
                      : `${myDashboardsCount} dashboard${myDashboardsCount !== 1 ? 's' : ''} available`}
                  </div>
                </div>
              </Tooltip>
              )}
              {canSeeSharedDashboards && (
              <Tooltip title="Access dashboards shared with you by collaborators" arrow placement="bottom">
                <div
                  className="home-action-card"
                  onClick={() => navigate('/dashboards?filter=shared')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigate('/dashboards?filter=shared');
                  }}
                >
                  <div className="home-action-card-icon">
                    <ShareOutlined sx={{ fontSize: 24, color: '#333' }} />
                  </div>
                  <div className="home-action-card-title">Shared Dashboards</div>
                  <div className="home-action-card-desc">
                    {loading
                      ? 'Loading...'
                      : `${sharedDashboardsCount} dashboard${sharedDashboardsCount !== 1 ? 's' : ''} shared with you`}
                  </div>
                </div>
              </Tooltip>
              )}
            </div>
          </div>
        </div>
        )}

        {/* ---- Surveys ---- */}
        {showSurveysSection && (
        <div className="home-cards-row home-animate-in home-animate-delay-4" style={{ marginTop: '1.5rem' }}>
          <div className="home-cards-col home-cards-col--start">
            <div className="home-section-header">
              <div className="home-section-accent" />
              <h2 className="home-section-title">Surveys</h2>
            </div>
            <Tooltip 
              title={
                canCreateSurvey
                  ? 'Create a new survey form'
                  : 'You do not have permission to create surveys'
              } 
              arrow 
              placement="bottom"
            >
              <div
                className={`home-action-card${!canCreateSurvey ? ' home-action-card--disabled' : ''}`}
                onClick={canCreateSurvey ? () => navigate('/survey/new') : undefined}
                role="button"
                tabIndex={canCreateSurvey ? 0 : -1}
                onKeyDown={(e) => {
                  if (canCreateSurvey && (e.key === 'Enter' || e.key === ' ')) navigate('/survey/new');
                }}
              >
                <div className="home-action-card-icon">
                  <AddIcon sx={{ fontSize: 24, color: '#333' }} />
                </div>
                <div className="home-action-card-title">Create Survey</div>
                <div className="home-action-card-desc">
                  Design and build a new survey from scratch.
                </div>
              </div>
            </Tooltip>
          </div>

          <div className="home-cards-col home-cards-col--dashboards">
            <div className="home-section-header" style={{ visibility: 'hidden' }} aria-hidden="true">
              <div className="home-section-accent" />
              <h2 className="home-section-title">Survey Management</h2>
            </div>
            <div className="home-cards-pair">
              {canManageSurveys && (
              <Tooltip title="Manage and edit your existing surveys" arrow placement="bottom">
                <div
                  className="home-action-card"
                  onClick={() => navigate('/survey/manage')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigate('/survey/manage');
                  }}
                >
                  <div className="home-action-card-icon">
                    <ListAltOutlined sx={{ fontSize: 24, color: '#333' }} />
                  </div>
                  <div className="home-action-card-title">Manage Surveys</div>
                  <div className="home-action-card-desc">
                    View, edit, and publish your surveys.
                  </div>
                </div>
              </Tooltip>
              )}
              {canViewSurveyResponses && (
              <Tooltip title="View responses for your active surveys" arrow placement="bottom">
                <div
                  className="home-action-card"
                  onClick={() => navigate('/survey/responses')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigate('/survey/responses');
                  }}
                >
                  <div className="home-action-card-icon">
                    <AssignmentOutlined sx={{ fontSize: 24, color: '#333' }} />
                  </div>
                  <div className="home-action-card-title">Survey Responses</div>
                  <div className="home-action-card-desc">
                    Analyze and review submitted responses.
                  </div>
                </div>
              </Tooltip>
              )}
            </div>
          </div>
        </div>
        )}


      </Container>

      <Dialog
        open={showDemo}
        onClose={() => {
          setShowDemo(false);
          if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'home-demo-dialog-paper' }}
        slotProps={{ backdrop: { className: 'home-demo-backdrop' } }}
      >
        <IconButton
          className="home-demo-close-btn"
          onClick={() => {
            setShowDemo(false);
            if (videoRef.current) {
              videoRef.current.pause();
              videoRef.current.currentTime = 0;
            }
          }}
          aria-label="Close demo video"
        >
          <CloseIcon />
        </IconButton>
        <div className="home-demo-video-wrap">
          <video
            ref={videoRef}
            className="home-demo-video"
            controls
            preload="metadata"
            src={`${process.env.PUBLIC_URL}/videos/SDA_demo.mp4`}
          >
            Your browser does not support the video element.
          </video>
        </div>
      </Dialog>
    </Box>
  );
};

export default HomePage;
